/**
 * EmptyState · 文件夹列表缺省页（推荐卡片版）
 *
 * 视觉与设计稿对齐（Figma 缺省页 260-33124）：
 *  · 顶部：紫色圆角方块 + sparkle 图标
 *  · 主标题「让 AI 帮你整理照片」
 *  · 待归类提示「团队内当前有 N 张照片待归类」（团队照片数同 Header NewStatistics）
 *  · 副标题「选择一种分类方式，AI 会基于团队内的照片自动分类」
 *  · 推荐分类卡片（RecommendCards · 竖排，含维值标签 + 末张多级卡）
 *  · 动作行「换一批」+「自己描述想要的分类方式」
 *  · 仅管理员可见的「或 手动新建文件夹」链接
 *
 * 行为：
 *  · 点击卡片：先 openSidebar + startNewConversation，再 sendUserMessage(prompt)
 *  · 「换一批」：向后翻一组（每组 4 张），翻完循环回第 1 组（useRecommendCards）
 *  · 「自己描述」：openSidebar + startNewConversation，进对话框自由描述
 *  · 「手动新建文件夹」：调父级注入的 onCreateFolder（非管理员未注入即不渲染）
 *  · 侧栏已展开时整个 empty 区返回 null —— 用户专注右侧对话，避免视觉重复
 *
 * 数据：
 *  · 推荐卡片来自 v2 init（/web/workgroup/folder/ai/v2/chat/init）的 recommend_cards，
 *    服务端预拼「单/单/单/多」序列，前端只按组取用；拉取中不渲染卡片区，
 *    失败 / 空序列走 FALLBACK_RECOMMEND_CARDS 兜底（useRecommendCards 内部处理）。
 */

import React from 'react'
import { EditOutlined, ReloadOutlined } from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { useGroupPhotoNumFormated } from '../../../../app/hooks/useGroupPhotoNumFormated'
import { openSidebar, selectSidebarOpen } from '../../../../slices/aiClassify'
import { useChatActions } from '../../components/AIChat/ChatActionsContext'
import RecommendCards from '../RecommendCards'
import { useRecommendCards } from '../RecommendCards/useRecommendCards'
import SparkleIcon from '../SparkleIcon'
import EmptyStateChatOpen from './EmptyStateChatOpen'
import { trackAIChatClick, trackClassifyClick } from '../../utils/trackClassify'
import styles from './FolderList.module.scss'

export interface EmptyStateProps {
  /**
   * 透传父级注入的「新建文件夹」回调
   *  · 非管理员场景父级不会注入（BFF 会拒，UI 隐藏入口）
   *  · 注入时 empty 页底部展示「或 手动新建文件夹」链接
   */
  onCreateFolder?: () => void
}

const EmptyState: React.FC<EmptyStateProps> = ({ onCreateFolder }) => {
  const dispatch = useAppDispatch()
  const sidebarOpen = useAppSelector(selectSidebarOpen)
  // 团队照片数（与 Header 统计同源）· 用于「团队内当前有 N 张照片待归类」提示
  const photoNum = useGroupPhotoNumFormated()
  const { sendUserMessage, startNewConversation } = useChatActions()
  // 推荐卡当前批 + 「换一批」轮换（v2 init 数据，loading 时先不渲染卡片区）
  const {
    cards: recommendCards,
    loading: recommendCardsLoading,
    canRefresh: canRefreshRecommend,
    refreshGroup
  } = useRecommendCards()

  // 侧栏已展开 → 展示「在右侧和 AI 助手对话」引导态（设计稿 12:3368）；
  // 侧栏关闭态（下方完整空态：推荐卡片 + 自定义输入）保持不变
  if (sidebarOpen) return <EmptyStateChatOpen onCreateFolder={onCreateFolder} />

  /**
   * 从建夹页发起一次 AI 分类
   *  · 先 startNewConversation：从 EmptyState 进来的任何 prompt 都视为「开个新话题」,
   *    不与已有会话上下文混淆（即便用户之前在侧栏聊过别的也不串台）
   *  · 同步 dispatch · startNewConversation 内部 conversationCleared 立即生效，
   *    紧跟着 sendUserMessage 写入的 user 消息会落到全新会话上
   */
  const launch = (prompt: string) => {
    trackAIChatClick('content', { string1: prompt })
    dispatch(openSidebar())
    startNewConversation()
    sendUserMessage(prompt)
  }

  /** 「换一批」· 向后翻一组推荐卡，翻完循环回第 1 组 */
  const handleRefresh = (): void => {
    refreshGroup()
  }

  /** 「自己描述想要的分类方式」· 打开助手侧栏进入新会话，用户在对话框自由描述 */
  const handleDescribe = (): void => {
    dispatch(openSidebar())
    startNewConversation()
  }

  return (
    // data-no-box-select · 整个 empty 区不参与框选，直接在根上标记，让 BoxSelect
    // 的 mousedown 处理器在命中前就 return（否则起框选会 preventDefault 拦截子级
    // mousedown，导致 empty 区内的 input 拿不到焦点）。
    <div className={styles.empty} data-no-box-select>
      <div className={styles.emptyInner}>
        <div className={styles.emptyHero} aria-hidden="true">
          <SparkleIcon />
        </div>
        <h2 className={styles.emptyTitle}>让 AI 帮你整理照片</h2>

        <p className={styles.emptyDesc}>
          {photoNum && (
            <>
              团队内当前有
              <strong className={styles.emptyPendingNum}>
                {photoNum.value}
              </strong>
              {photoNum.unit}
              照片待归类，
            </>
          )}
          选择一种分类方式，AI 会基于团队内的照片自动分类
        </p>

        {!recommendCardsLoading && (
          <RecommendCards
            cards={recommendCards}
            onSelectCard={launch}
            className={styles.emptyOptionGrid}
          />
        )}

        <div className={styles.emptyActions}>
          {/* 「换一批」仅在有下一批（>4 张）时渲染 */}
          {canRefreshRecommend && (
            <button
              type="button"
              className={styles.emptyActionBtn}
              onClick={handleRefresh}
            >
              <ReloadOutlined aria-hidden="true" />
              换一批
            </button>
          )}
          <button
            type="button"
            className={styles.emptyActionBtn}
            onClick={handleDescribe}
          >
            <EditOutlined aria-hidden="true" />
            自己描述想要的分类方式
          </button>
        </div>

        {onCreateFolder && (
          <div className={styles.emptyFallback}>
            <span className={styles.emptyFallbackHint}>或</span>
            <button
              type="button"
              className={styles.emptyFallbackLink}
              onClick={() => {
                trackClassifyClick('createFolder')
                onCreateFolder()
              }}
            >
              手动新建文件夹
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default EmptyState
